import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import AddPropertyModal from './AddPropertyModal';
import { getViewerTimezone } from '../../utils/dateTime';
import './TransactionProperties.css';

const TransactionProperties = ({ transaction, accessControl, onPropertiesLoaded }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rpaCreatingId, setRpaCreatingId] = useState(null);
  const [rpaUpdatingId, setRpaUpdatingId] = useState(null);

  // RPA modal state
  const [rpaModal, setRpaModal] = useState({ open: false, step: 1, prop: null });
  const [libraries, setLibraries] = useState([]);
  const [libsLoading, setLibsLoading] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [selectedLib, setSelectedLib] = useState(null);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formSearch, setFormSearch] = useState('');
  const [addingFormId, setAddingFormId] = useState(null);

  // Add/Edit Property modal state (manual entry — no MLS lookup involved)
  const [addPropertyModal, setAddPropertyModal] = useState({ open: false, editingProperty: null });
  const [removingId, setRemovingId] = useState(null);
  // Remove-property confirmation modal (replaces window.confirm with the
  // site's own popup chrome — see requestRemoveProperty/confirmRemoveProperty).
  const [removeConfirm, setRemoveConfirm] = useState({ open: false, prop: null });

  // Determine if this is a BUYING/PURCHASE transaction
  const transactionType = transaction?.type?.toUpperCase() || transaction?.side?.toUpperCase() || '';
  const isBuyingTransaction = transactionType === 'BUYING' || transactionType === 'PURCHASE';

  // Check if current user is Agent or Transaction Coordinator
  const isAgentOrTC = accessControl?.isAgent() || false;

  // Check if any property already has an RPA created (used to decide Create vs Update)
  const hasExistingRPA = properties.some(p => p.rpaCreated);

  // Function to get Firebase token
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (err) {
            reject(err);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Enrich interested properties with MLS data (photos, details)
  const enrichWithMlsData = async (propertiesList, firebaseToken) => {
    const enriched = await Promise.all(
      propertiesList.map(async (prop) => {
        const existingPhoto = prop.photoUrl || prop.mainPhoto || prop.imageUrl ||
          prop.thumbnailUrl || prop.primaryPhoto ||
          (Array.isArray(prop.photos) && prop.photos.length > 0) ||
          (Array.isArray(prop.images) && prop.images.length > 0) ||
          (Array.isArray(prop.media) && prop.media.length > 0);
        if (existingPhoto) return prop;

        const mlsId = prop.mlsId || prop.mlsNumber || prop.listingId;
        if (!mlsId) return prop;

        try {
          const response = await apiClient.get(
            API_ENDPOINTS.mlsProperty(mlsId),
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          const mlsData = Array.isArray(response.data) ? response.data[0] : response.data;
          if (mlsData) {
            return { ...prop, ...mlsData, ...{ id: prop.id, status: prop.status } };
          }
        } catch (err) {
          console.warn(`Failed to fetch MLS data for ${mlsId}:`, err.message);
        }
        return prop;
      })
    );
    return enriched;
  };

  // Fetch interested properties
  const fetchInterestedProperties = useCallback(async () => {
    if (!transaction?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const firebaseToken = await getFirebaseToken();

      const response = await apiClient.get(
        API_ENDPOINTS.interestedProperties(transaction.id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = response.data;
      let propertiesList = [];

      if (Array.isArray(data)) {
        propertiesList = data;
      } else if (data && Array.isArray(data.data)) {
        propertiesList = data.data;
      } else if (data && Array.isArray(data.properties)) {
        propertiesList = data.properties;
      } else if (data && Array.isArray(data.results)) {
        propertiesList = data.results;
      } else if (data && Array.isArray(data.content)) {
        propertiesList = data.content;
      } else if (data && typeof data === 'object' && data.id) {
        propertiesList = [data];
      } else if (data && typeof data === 'object') {
        const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
        if (arrayKey) {
          propertiesList = data[arrayKey];
        }
      }

      const enrichedProperties = await enrichWithMlsData(propertiesList, firebaseToken);
      setProperties(enrichedProperties);
      const firstId = enrichedProperties[0]?.id || enrichedProperties[0]?.interestedPropertyId || null;
      onPropertiesLoaded?.(enrichedProperties.length, firstId);
    } catch (err) {
      console.error('Error fetching interested properties:', err);
      if (err.response?.status === 404) {
        setProperties([]);
        onPropertiesLoaded?.(0, null);
      } else {
        setError('Failed to load properties. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [transaction?.id, getFirebaseToken]);

  useEffect(() => {
    fetchInterestedProperties();
  }, [fetchInterestedProperties]);

  // ── Add / Edit Property (manual entry) ──────────────────────────────────────
  const openAddPropertyModal = () => setAddPropertyModal({ open: true, editingProperty: null });
  const openEditPropertyModal = (prop) => setAddPropertyModal({ open: true, editingProperty: prop });
  const closeAddPropertyModal = () => setAddPropertyModal({ open: false, editingProperty: null });
  const handlePropertySaved = () => {
    closeAddPropertyModal();
    fetchInterestedProperties();
  };

  // Opens the confirm modal below — the actual delete call only happens if
  // the user hits "Yes" there (confirmRemoveProperty).
  const requestRemoveProperty = (prop) => {
    if (prop.rpaCreated || removingId) return;
    setRemoveConfirm({ open: true, prop });
  };

  const closeRemoveConfirm = () => {
    if (removingId) return; // don't allow dismissing mid-delete
    setRemoveConfirm({ open: false, prop: null });
  };

  const confirmRemoveProperty = async () => {
    const prop = removeConfirm.prop;
    if (!prop || removingId) return;
    setRemovingId(prop.id);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteInterestedProperty(transaction.id, prop.id),
        { headers: { Authorization: `Bearer ${firebaseToken}` } }
      );
      setRemoveConfirm({ open: false, prop: null });
      await fetchInterestedProperties();
    } catch (err) {
      console.error('Failed to remove property:', err);
      alert(err.response?.data?.message || err.message || 'Failed to remove property. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  // ── Close RPA Modal ─────────────────────────────────────────────────────────
  const closeRpaModal = () => {
    if (addingFormId) return;
    setRpaModal({ open: false, step: 1, prop: null });
    setLibraries([]);
    setForms([]);
    setSelectedLib(null);
    setLibSearch('');
    setFormSearch('');
  };

  // ── Get user's license state from localStorage ──────────────────────────────
  const getUserState = () => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return null;
      const user = JSON.parse(stored);
      // stateCode is the two-letter abbreviation (e.g. "CA"); fall back to state field
      const state = user.stateCode || user.state || user.licenseState || null;
      return state || null;
    } catch {
      return null;
    }
  };

  // ── Create RPA — open Form Library picker ───────────────────────────────────
  const handleCreateRPA = async (prop) => {
    setRpaModal({ open: true, step: 1, prop });
    setLibSearch('');
    setLibraries([]);
    setLibsLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const state = getUserState();
      console.log('Fetching DocuSign form libraries', state ? `for state: ${state}` : '');
      const params = state ? { state } : {};
      const resp = await apiClient.get(
        API_ENDPOINTS.docusignFormLibraries,
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          params,
          timeout: 120000,
        }
      );
      console.log('Form libraries fetched successfully');
      const data = resp.data;
      let libs = [];
      if (Array.isArray(data)) libs = data;
      else if (Array.isArray(data?.formsLibrarySummaries)) libs = data.formsLibrarySummaries;
      else if (Array.isArray(data?.data)) libs = data.data;
      setLibraries(libs);
    } catch (err) {
      console.error('Failed to load form libraries:', err);
      alert('Failed to load form libraries. Please try again.');
      setRpaModal({ open: false, step: 1, prop: null });
    } finally {
      setLibsLoading(false);
    }
  };

  // ── Select a Form Library — load its forms ──────────────────────────────────
  const handleSelectLibrary = async (lib) => {
    setSelectedLib(lib);
    setRpaModal(prev => ({ ...prev, step: 2 }));
    setFormSearch('');
    setForms([]);
    setFormsLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const resp = await apiClient.get(
        API_ENDPOINTS.docusignFormLibraryForms(lib.formsLibraryId),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 120000 }
      );
      const data = resp.data;
      let formsList = [];
      if (Array.isArray(data)) formsList = data;
      else if (Array.isArray(data?.forms)) formsList = data.forms;
      else if (Array.isArray(data?.data)) formsList = data.data;
      setForms(formsList);
    } catch (err) {
      console.error('Failed to load forms:', err);
      alert('Failed to load forms. Please try again.');
      setRpaModal(prev => ({ ...prev, step: 1 }));
    } finally {
      setFormsLoading(false);
    }
  };

  // ── Select a Form — ensureRoom + addForm + markRpaCreated ───────────────────
  const handleSelectForm = async (form) => {
    if (addingFormId) return;
    const prop = rpaModal.prop;
    setAddingFormId(form.libraryFormId);
    try {
      const firebaseToken = await getFirebaseToken();

      // Step 1: Ensure DocuSign room exists with property context
      try {
        const rawState = prop.state || prop.propertyState;
        const formattedState = rawState
          ? (rawState.startsWith('US-') ? rawState : `US-${rawState}`)
          : undefined;
        await apiClient.post(
          API_ENDPOINTS.syncDocuSignRoom(transaction.id),
          {
            mlsId: prop.mlsId || prop.mlsNumber,
            address1: prop.address || prop.propertyAddress || prop.streetAddress,
            city: prop.city || prop.propertyCity,
            state: formattedState,
            postalCode: prop.zipCode || prop.propertyZip || prop.postalCode || prop.zip,
            listPrice: prop.listPrice || prop.price,
            ...(prop.listingAgentEmail && {
              listingAgent: {
                name: prop.listingAgentName,
                email: prop.listingAgentEmail,
                phone: prop.listingAgentPhone,
                company: prop.listingOfficeName,
              }
            }),
          },
          { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
        );
      } catch (roomErr) {
        console.warn('syncDocuSignRoom failed (continuing):', roomErr.message);
      }

      // Step 2: Add form to transaction
      const addResp = await apiClient.post(
        API_ENDPOINTS.addDocumentForm(transaction.id),
        { formId: form.libraryFormId },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      const docData = addResp.data;
      const docId = docData?.documentId?.toString() || docData?.id?.toString() || null;

      // Step 3: Mark RPA as created
      await apiClient.post(
        API_ENDPOINTS.markRpaCreated(transaction.id, prop.id),
        docId ? { rpaDocumentId: docId } : {},
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );

      closeRpaModal();
      await fetchInterestedProperties();
      alert(`"${form.name}" has been added to the transaction.`);
    } catch (err) {
      console.error('Failed to add form:', err);
      const msg = err.response?.data?.message || err.message || '';
      if (
        msg.includes('FORM_ALREADY_EXISTS') ||
        msg.includes('FORM_ALREADY_IN_ROOM') ||
        msg.includes('already in room') ||
        msg.includes('already been added')
      ) {
        alert(`"${form.name}" has already been added to this transaction.`);
      } else {
        alert(msg || 'Failed to add form. Please try again.');
      }
    } finally {
      setAddingFormId(null);
    }
  };

  // ── Update RPA ──────────────────────────────────────────────────────────────
  const handleUpdateRPA = async (prop) => {
    const mlsId = getPropertyMls(prop);
    if (!window.confirm(`Are you sure you want to update the RPA to use property ${mlsId}?`)) return;
    if (rpaUpdatingId) return;
    setRpaUpdatingId(prop.id);
    try {
      const firebaseToken = await getFirebaseToken();

      // Format state for DocuSign (expects "US-CA" format)
      const rawState = prop.state || prop.propertyState;
      const formattedState = rawState
        ? (rawState.startsWith('US-') ? rawState : `US-${rawState}`)
        : undefined;

      // Step 1: Update DocuSign room property details
      await apiClient.put(
        API_ENDPOINTS.updatePropertyDetails(transaction.id),
        {
          mlsId,
          address1: prop.address || prop.propertyAddress || prop.streetAddress,
          city: prop.city || prop.propertyCity,
          state: formattedState,
          postalCode: prop.zipCode || prop.propertyZip || prop.postalCode || prop.zip,
          county: prop.county,
          country: prop.country,
          listPrice: prop.listPrice || prop.price,
          bedroomsTotal: prop.bedrooms || prop.beds,
          bathroomsTotal: prop.bathrooms || prop.baths,
          area: prop.squareFeet || prop.sqft || prop.livingArea,
          yearBuilt: prop.yearBuilt,
          lotSize: prop.lotSize,
          lotSizeAcres: prop.lotSizeAcres,
          garageSpaces: prop.garageSpaces,
          stories: prop.stories,
          propertyType: prop.propertyType,
          schoolDistrict: prop.schoolDistrict,
          taxAnnualAmount: prop.taxAnnualAmount,
          apn: prop.taxId,
          ...(prop.listingAgentName && {
            listingAgent: {
              name: prop.listingAgentName,
              email: prop.listingAgentEmail,
              phone: prop.listingAgentPhone,
              company: prop.listingOfficeName,
            }
          }),
        },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );

      // Step 2: Mark the new property as RPA created
      await apiClient.post(
        API_ENDPOINTS.markRpaCreated(transaction.id, prop.id),
        {},
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );

      await fetchInterestedProperties();
      alert('RPA property details have been updated successfully.');
    } catch (err) {
      console.error('Failed to update RPA:', err);
      alert(err.response?.data?.error || err.message || 'Failed to update RPA property details.');
    } finally {
      setRpaUpdatingId(null);
    }
  };

  // ── Property field helpers ─────────────────────────────────────────────────
  const safeStr = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return '';
    return String(val);
  };

  const getPropertyAddress = (prop) => {
    return safeStr(prop.address || prop.propertyAddress || prop.streetAddress || prop.fullAddress || prop.name) || 'Unknown Address';
  };

  const getPropertyMls = (prop) => safeStr(prop.mlsId || prop.mlsNumber || prop.listingId || prop.id || '');

  // Mirrors mobile app's getSourceLabel in InterestedPropertiesTab.tsx.
  // Uses regex (not new URL()) to match the mobile approach and avoid failures
  // on malformed or short URLs.
  const getSourceLabel = (url) => {
    if (!url) return 'View Listing';
    const m = url.match(/^https?:\/\/([^/?#]+)/i);
    const hostname = (m?.[1] || '').toLowerCase();
    if (hostname.includes('zillow.com') || hostname === 'zillow.me') return 'View on Zillow';
    if (hostname.includes('redfin.com') || hostname === 'redf.in')   return 'View on Redfin';
    if (hostname.includes('realtor.com'))  return 'View on Realtor.com';
    if (hostname.includes('trulia.com'))   return 'View on Trulia';
    if (hostname.includes('homes.com'))    return 'View on Homes.com';
    return 'View Listing';
  };

  // Mirrors mobile app's buildAddressSearchUrl in property.service.ts.
  // Redfin's homepage resolves a free-form address query to the matching
  // property page, so this serves as a reliable fallback when no sourceUrl
  // was stored (e.g. property added via MLS search rather than a pasted link).
  const buildAddressSearchUrl = (prop) => {
    const query = [
      prop.propertyAddress || prop.address || prop.streetAddress,
      prop.propertyCity || prop.city,
      prop.propertyState || prop.state,
      prop.propertyZip || prop.zipCode || prop.postalCode || prop.zip,
    ].filter(Boolean).join(' ').trim();
    if (!query) return null;
    return `https://www.redfin.com/?searchInput=${encodeURIComponent(query)}`;
  };

  const getPropertyPrice = (prop) => {
    const price = prop.listPrice || prop.price || prop.askingPrice || 0;
    if (!price) return 'Price N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const getPropertyDetails = (prop) => {
    const beds = prop.bedrooms || prop.beds || 0;
    const baths = prop.bathrooms || prop.baths || 0;
    const sqft = prop.squareFeet || prop.sqft || prop.livingArea || prop.area || 0;
    const parts = [];
    if (beds) parts.push(`${beds} bed`);
    if (baths) parts.push(`${baths} bath`);
    if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
    return parts.join(' \u2022 ') || 'Details N/A';
  };

  const getPropertyLocation = (prop) => {
    const city = safeStr(prop.city || prop.propertyCity);
    const state = safeStr(prop.state || prop.propertyState);
    const zip = safeStr(prop.zipCode || prop.postalCode || prop.zip || prop.propertyZip);
    const parts = [city, state].filter(Boolean).join(', ');
    return zip ? `${parts} ${zip}` : parts || 'Location N/A';
  };

  const getPropertyStatus = (prop) => safeStr(prop.status || prop.listingStatus || '');

  const getPropertyBadgeClass = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'NEW': return 'new';
      case 'PENDING': return 'pending';
      case 'OPEN_HOUSE': return 'open-house';
      case 'PRICE_REDUCED': return 'price-reduced';
      default: return 'active';
    }
  };

  const getPropertyEmoji = (index) => {
    const emojis = ['\uD83C\uDFE0', '\uD83C\uDFE1', '\uD83C\uDFD8\uFE0F'];
    return emojis[index % emojis.length];
  };

  const getPropertyPhoto = (prop) => {
    if (!prop) return '';
    if (prop.primaryPhoto) return prop.primaryPhoto;
    if (prop.photoUrl) return prop.photoUrl;
    if (prop.mainPhoto) return prop.mainPhoto;
    if (prop.imageUrl) return prop.imageUrl;
    if (Array.isArray(prop.photos) && prop.photos.length > 0) {
      const first = prop.photos[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    return '';
  };

  const getPhotoCount = (prop) => (Array.isArray(prop?.photos) ? prop.photos.length : 0);

  // Filtered lists for modal
  const filteredLibraries = libraries.filter(lib =>
    !libSearch || lib.name?.toLowerCase().includes(libSearch.toLowerCase())
  );
  const filteredForms = forms.filter(f =>
    !formSearch || f.name?.toLowerCase().includes(formSearch.toLowerCase())
  );

  return (
    <div className="prop-search-area">
      {/* Header */}
      <div className="prop-content-header">
        <h2 className="prop-page-title">Selected Properties</h2>
        {isBuyingTransaction && isAgentOrTC && (
          <button className="prop-add-property-btn" onClick={openAddPropertyModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Property
          </button>
        )}
      </div>

      {/* Properties Content */}
      <div className="prop-scroll-content">
        {isLoading ? (
          <div className="prop-loading">
            <div className="prop-loading-spinner"></div>
            <span>Loading properties...</span>
          </div>
        ) : error ? (
          <div className="prop-error">
            <div className="prop-empty-icon">&#9888;&#65039;</div>
            <h3>Error Loading Properties</h3>
            <p>{error}</p>
            <button className="prop-retry-btn" onClick={fetchInterestedProperties}>
              Try Again
            </button>
          </div>
        ) : properties.length === 0 ? (
          <div className="prop-empty">
            <div className="prop-empty-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="1.5">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h3>No Properties Found</h3>
            <p>No interested properties for this transaction yet.</p>
          </div>
        ) : (
          <div className="prop-grid">
            {properties.map((prop, index) => {
              const status = getPropertyStatus(prop);
              const badgeClass = getPropertyBadgeClass(status);
              const photoCount = getPhotoCount(prop);
              const dom = prop.daysOnMarket || 0;
              const isCreatingThis = rpaCreatingId === prop.id;
              const isUpdatingThis = rpaUpdatingId === prop.id;
              const anyRpaActionInProgress = rpaCreatingId !== null || rpaUpdatingId !== null;

              return (
                <div className="prop-card" key={prop.id || prop.mlsId || index}>
                  <div className="prop-card-image">
                    {getPropertyPhoto(prop) ? (
                      <img
                        src={getPropertyPhoto(prop)}
                        alt={getPropertyAddress(prop)}
                        className="prop-card-img"
                      />
                    ) : (
                      <div className="prop-card-img-placeholder">{getPropertyEmoji(index)}</div>
                    )}
                    {status && (
                      <div className={`prop-card-badge ${badgeClass}`}>
                        {status.toUpperCase()}
                      </div>
                    )}
                    {prop.source === 'MANUAL' && (
                      <div className="prop-source-badge">Manually Added</div>
                    )}
                    {photoCount > 0 && (
                      <div className="prop-photo-count">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> {photoCount}
                      </div>
                    )}
                    {dom > 0 && (
                      <div className="prop-dom-badge">{dom} days</div>
                    )}
                  </div>

                  {/* RPA Created indicator badge on card */}
                  {prop.rpaCreated && (
                    <div className="prop-rpa-indicator">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                      </svg>
                      RPA Created
                    </div>
                  )}

                  <div className="prop-card-info">
                    <div className="prop-card-price">{getPropertyPrice(prop)}</div>
                    <div className="prop-card-address">{getPropertyAddress(prop)}</div>
                    <div className="prop-card-location">{getPropertyLocation(prop)}</div>
                    {/* Manually-added properties have no real MLS number — getPropertyMls()
                        falls back to the internal record id, which isn't an MLS# and
                        shouldn't be shown as one. */}
                    {prop.source !== 'MANUAL' && (
                      <div className="prop-card-mls">MLS# {getPropertyMls(prop)}</div>
                    )}
                    <div className="prop-card-specs">
                      {getPropertyDetails(prop)}
                    </div>

                    {(() => {
                      // Manually-added properties have no external MLS listing to link
                      // out to — "View Listing" instead opens our own property details
                      // page, matching the same route the "View Details" button (below)
                      // uses for non-manual properties.
                      if (prop.source === 'MANUAL') {
                        const propId = getPropertyMls(prop) || safeStr(prop.interestedPropertyId);
                        if (!transaction?.id || !propId) return null;
                        const internalPath = `/transaction/${transaction.id}/property/${propId}`;
                        return (
                          <a
                            href={internalPath}
                            className="prop-card-source-link"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(internalPath, { state: { propertyData: prop } });
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            View Listing
                          </a>
                        );
                      }
                      // Mirrors mobile: item.sourceUrl || buildSearchUrl(item)
                      const sourceUrl = prop.sourceUrl || prop.zillowUrl || prop.listingUrl || prop.externalUrl || prop.url || prop.sourceLink || prop.propertyUrl;
                      const linkUrl = sourceUrl || buildAddressSearchUrl(prop);
                      // Derive label only from the explicitly stored source URL so that
                      // "View on Zillow" / "View on Redfin" appear only when the property
                      // was actually linked from that site. The Redfin search fallback
                      // (when sourceUrl is absent) gets the neutral "View Listing" label.
                      const linkLabel = getSourceLabel(sourceUrl);
                      return linkUrl ? (
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="prop-card-source-link"
                          onClick={e => e.stopPropagation()}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                            <polyline points="15 3 21 3 21 9"/>
                            <line x1="10" y1="14" x2="21" y2="3"/>
                          </svg>
                          {linkLabel}
                        </a>
                      ) : null;
                    })()}

                    <div className="prop-card-actions">
{/*                       
                      {isBuyingTransaction && isAgentOrTC && (
                        <>
                          {prop.rpaCreated ? (
                            <button className="prop-action-btn prop-action-btn-view-rpa" disabled>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                              <span>View RPA</span>
                            </button>
                          ) : hasExistingRPA ? (
                            <button
                              className="prop-action-btn prop-action-btn-update"
                              onClick={() => handleUpdateRPA(prop)}
                              disabled={anyRpaActionInProgress}
                            >
                              {isUpdatingThis ? (
                                <>
                                  <div className="prop-btn-spinner"></div>
                                  <span>Updating...</span>
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                  <span>Update RPA</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              className="prop-action-btn prop-action-btn-rpa"
                              onClick={() => handleCreateRPA(prop)}
                              disabled={anyRpaActionInProgress || rpaModal.open}
                            >
                              {isCreatingThis ? (
                                <>
                                  <div className="prop-btn-spinner prop-btn-spinner-white"></div>
                                  <span>Creating...</span>
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="12" y1="18" x2="12" y2="12"/>
                                    <line x1="9" y1="15" x2="15" y2="15"/>
                                  </svg>
                                  <span>Create RPA</span>
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )} */}

                      {/* Manually-added properties have no MLS-backed detail page to
                          navigate to — offer Edit instead (reopens this same modal,
                          pre-filled). Locked once an RPA exists on the property,
                          matching the backend's own "can't remove/mutate after RPA
                          created" rule (there's no separate update endpoint — Edit
                          saves by deleting + re-adding, so this guard also protects
                          against orphaning RPA state). */}
                      {prop.source === 'MANUAL' ? (
                        <button
                          className="prop-action-btn prop-action-btn-secondary"
                          onClick={() => openEditPropertyModal(prop)}
                          disabled={!!prop.rpaCreated}
                          title={prop.rpaCreated ? 'RPA already created for this property' : undefined}
                        >
                          Edit
                        </button>
                      ) : (
                        <button
                          className="prop-action-btn prop-action-btn-primary"
                          onClick={() => {
                            const propId = getPropertyMls(prop) || safeStr(prop.interestedPropertyId);
                            if (transaction?.id && propId) {
                              navigate(`/transaction/${transaction.id}/property/${propId}`, {
                                state: { propertyData: prop }
                              });
                            }
                          }}
                        >
                          View Details
                        </button>
                      )}

                      {isAgentOrTC && (
                        <button
                          className="prop-action-btn prop-action-btn-remove"
                          onClick={() => requestRemoveProperty(prop)}
                          disabled={!!prop.rpaCreated || removingId === prop.id}
                          title={prop.rpaCreated ? 'Cannot remove a property that already has an RPA created' : undefined}
                        >
                          {removingId === prop.id ? 'Removing…' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── RPA Form Library / Form Selection Modal ── */}
      {rpaModal.open && (
        <div className="prop-modal-overlay" onClick={closeRpaModal}>
          <div className="prop-rpa-modal" onClick={e => e.stopPropagation()}>

            {/* ── Step 1: Select Form Library ── */}
            {rpaModal.step === 1 && (
              <>
                <div className="prop-rpa-modal-header">
                  <h3 className="prop-rpa-modal-title">Select Form Library</h3>
                  <button className="prop-rpa-modal-close" onClick={closeRpaModal} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>

                <div className="prop-rpa-modal-search-bar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by state or association..."
                    value={libSearch}
                    onChange={e => setLibSearch(e.target.value)}
                    className="prop-rpa-modal-search-input"
                    autoFocus
                  />
                  {libSearch && (
                    <button className="prop-rpa-modal-search-clear" onClick={() => setLibSearch('')} aria-label="Clear search">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>

                <div className="prop-rpa-modal-body">
                  {libsLoading ? (
                    <div className="prop-rpa-modal-loading">
                      <div className="prop-loading-spinner"></div>
                      <span>Loading libraries...</span>
                    </div>
                  ) : filteredLibraries.length === 0 ? (
                    <div className="prop-rpa-modal-empty">
                      {libSearch ? 'No libraries match your search.' : 'No form libraries available.'}
                    </div>
                  ) : (
                    <>
                      {libSearch && (
                        <div className="prop-rpa-modal-count">
                          {filteredLibraries.length} librar{filteredLibraries.length === 1 ? 'y' : 'ies'} found
                        </div>
                      )}
                      <div className="prop-rpa-modal-list">
                        {filteredLibraries.map(lib => (
                          <button
                            key={lib.formsLibraryId}
                            className="prop-rpa-lib-item"
                            onClick={() => handleSelectLibrary(lib)}
                          >
                            <div className="prop-rpa-lib-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                              </svg>
                            </div>
                            <div className="prop-rpa-lib-info">
                              <div className="prop-rpa-lib-name">{lib.name}</div>
                              {lib.formCount != null && (
                                <div className="prop-rpa-lib-meta">
                                  {lib.formCount} form{lib.formCount !== 1 ? 's' : ''} available
                                </div>
                              )}
                            </div>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2" className="prop-rpa-lib-chevron">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* ── Step 2: Select Form ── */}
            {rpaModal.step === 2 && (
              <>
                <div className="prop-rpa-modal-header prop-rpa-modal-header-step2">
                  <button
                    className="prop-rpa-modal-back"
                    onClick={() => { setRpaModal(prev => ({ ...prev, step: 1 })); setFormSearch(''); }}
                    aria-label="Back"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <h3 className="prop-rpa-modal-title prop-rpa-modal-title-truncate">
                    {selectedLib?.name || 'Select Form'}
                  </h3>
                  <button className="prop-rpa-modal-close" onClick={closeRpaModal} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>

                <div className="prop-rpa-modal-search-bar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search forms..."
                    value={formSearch}
                    onChange={e => setFormSearch(e.target.value)}
                    className="prop-rpa-modal-search-input"
                    autoFocus
                  />
                  {formSearch && (
                    <button className="prop-rpa-modal-search-clear" onClick={() => setFormSearch('')} aria-label="Clear search">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>

                <div className="prop-rpa-modal-body">
                  {formsLoading ? (
                    <div className="prop-rpa-modal-loading">
                      <div className="prop-loading-spinner"></div>
                      <span>Loading forms...</span>
                    </div>
                  ) : filteredForms.length === 0 ? (
                    <div className="prop-rpa-modal-empty">
                      {formSearch ? 'No forms match your search.' : 'No forms available in this library.'}
                    </div>
                  ) : (
                    <>
                      <div className="prop-rpa-modal-count">
                        {formSearch
                          ? `${filteredForms.length} form${filteredForms.length !== 1 ? 's' : ''} found`
                          : `${filteredForms.length} form${filteredForms.length !== 1 ? 's' : ''} available`
                        }
                      </div>
                      <div className="prop-rpa-modal-list">
                        {filteredForms.map(form => {
                          const isAdding = addingFormId === form.libraryFormId;
                          const lastUpdated = form.lastModifiedDate || form.lastUpdatedDate;
                          return (
                            <button
                              key={form.libraryFormId}
                              className="prop-rpa-form-item"
                              onClick={() => handleSelectForm(form)}
                              disabled={!!addingFormId}
                            >
                              <div className="prop-rpa-form-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                              </div>
                              <div className="prop-rpa-form-info">
                                <div className="prop-rpa-form-name">{form.name}</div>
                                {lastUpdated && (
                                  <div className="prop-rpa-form-meta">
                                    Updated {new Date(lastUpdated).toLocaleDateString('en-US', { timeZone: getViewerTimezone() })}
                                  </div>
                                )}
                              </div>
                              <div className="prop-rpa-form-action">
                                {isAdding ? (
                                  <div className="prop-btn-spinner"></div>
                                ) : (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="16"/>
                                    <line x1="8" y1="12" x2="16" y2="12"/>
                                  </svg>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* ── Add / Edit Property Modal (manual entry) ── */}
      {addPropertyModal.open && (
        <AddPropertyModal
          transaction={transaction}
          editingProperty={addPropertyModal.editingProperty}
          getFirebaseToken={getFirebaseToken}
          onClose={closeAddPropertyModal}
          onSaved={handlePropertySaved}
        />
      )}

      {/* ── Remove Property Confirmation — replaces window.confirm with the
          site's own modal chrome (same header/close-button/action-button
          classes as the Add/Edit and RPA modals above). ── */}
      {removeConfirm.open && removeConfirm.prop && (
        <div className="prop-modal-overlay" onClick={closeRemoveConfirm}>
          <div className="prop-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="prop-rpa-modal-header">
              <h3 className="prop-rpa-modal-title" style={{ textAlign: 'left' }}>
                Remove Property
              </h3>
              <button
                className="prop-rpa-modal-close"
                onClick={closeRemoveConfirm}
                disabled={!!removingId}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="prop-confirm-modal-body">
              <p className="prop-confirm-modal-text">
                Remove <strong>{getPropertyAddress(removeConfirm.prop)}</strong> from Selected Properties?
              </p>
            </div>

            <div className="prop-confirm-modal-actions">
              <button
                className="prop-action-btn prop-action-btn-secondary"
                onClick={closeRemoveConfirm}
                disabled={!!removingId}
              >
                No
              </button>
              <button
                className="prop-action-btn prop-action-btn-primary"
                onClick={confirmRemoveProperty}
                disabled={!!removingId}
              >
                {removingId ? 'Removing…' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionProperties;
